import React from "react";
import {
  Box,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  VStack,
} from "@chakra-ui/react";
import { FaTriangleExclamation } from "react-icons/fa6";

import { RouterLink, Typography } from "@tether-web-portal/components/common";

export interface UnconfiguredRoomsWarningProps {
  rooms: { id: string; name: string }[];
}

/**
 * Orange warning icon rendered next to the Total Occupancy tile title when
 * any contributing room has no configured capacity. The room's people-count
 * still inflates the tile's `occupied / total` (by design — the people are
 * physically there) but the percentage doesn't account for them, so the
 * popover explains the inflation and links to each room so operators can
 * set a capacity.
 *
 * Uses Popover rather than Tooltip because the content is interactive —
 * Tooltip closes as soon as the pointer leaves the trigger, making the
 * room links unclickable. Popover with `trigger="hover"` stays open while
 * the pointer is over either the trigger or the popover content, so the
 * user can mouse across to click a room link without the panel closing.
 *
 * The PopoverContent is rendered inside a Portal so it escapes the tile's
 * `overflow: hidden` wrapper — otherwise the popover gets clipped by the
 * tile card boundary.
 */
export const UnconfiguredRoomsWarning: React.FC<UnconfiguredRoomsWarningProps> = ({
  rooms,
}) => {
  if (!rooms.length) return null;

  return (
    <Popover trigger="hover" placement="bottom-start" openDelay={100} closeDelay={150}>
      <PopoverTrigger>
        <Box
          as="span"
          display="inline-flex"
          alignItems="center"
          cursor="help"
          tabIndex={0}
          aria-label={`${rooms.length} room${rooms.length === 1 ? "" : "s"} without configured capacity`}
        >
          <FaTriangleExclamation color="#ED8936" size={22} />
        </Box>
      </PopoverTrigger>
      <Portal>
        <PopoverContent bg="gray.700" color="white" borderColor="gray.700" maxW="340px" zIndex="popover">
          <PopoverArrow bg="gray.700" />
          <PopoverBody>
            <VStack alignItems="flex-start" spacing={1}>
              <Typography size="sm" fontWeight="bold" color="white">
                Total Occupancy is inflated
              </Typography>
              <Typography size="sm" color="white">
                The following room{rooms.length === 1 ? "" : "s"}{" "}
                {rooms.length === 1 ? "has" : "have"} occupants but no configured capacity.
                Their people counts are included in the total but don&apos;t contribute to
                the denominator, so the percentage reads higher than the building can
                physically hold.
              </Typography>
              <Typography size="sm" color="white" mt={2} fontWeight="bold">
                Set a capacity on:
              </Typography>
              <VStack alignItems="flex-start" spacing={0} pl={2}>
                {rooms.map((r) => (
                  <RouterLink
                    key={r.id}
                    to={`/area/room/${r.id}/edit`}
                    style={{ color: "white", textDecoration: "underline" }}
                  >
                    <Typography size="sm" color="white">
                      • {r.name}
                    </Typography>
                  </RouterLink>
                ))}
              </VStack>
            </VStack>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};
